import { forEach, has, isArray, isEmpty, isNil, isString, some } from './lodash';

/**
 * json字符串parse
 *
 * @param value
 * @param onFail 执行出错的回调
 */
export const JSONParse = (value: any, onFail?: (e: any) => void) => {
    try {
        return JSON.parse(value);
    } catch (e) {
        if (onFail) {
            onFail(e);
        }
        return value;
    }
};

/**
 * UUID 生成器
 *
 * @param [buffer]
 * @returns {string} - UUID
 * @see {@link https://gist.github.com/jed/982883}
 */
export const getUUID = (buffer?: any) => {
    return buffer
        ? (buffer ^ ((Math.random() * 16) >> (buffer / 4))).toString(16)
        : (String(1e7) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, getUUID);
};

/**
 * 数组包裹
 *
 * @param obj
 */
export const makeArray = (obj: any) => {
    if (isNil(obj)) {
        return [];
    }
    if (isArray(obj)) {
        return obj;
    } else {
        return [obj];
    }
};
/**
 * 加载脚本
 *
 * @param src
 * @param callback
 */
export const loadScript = (src: string, callback: () => void) => {
    if (!document.querySelector("script[src^='" + src + "']")) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        document.body.appendChild(script);
        script.addEventListener('load', callback);
    } else {
        callback && callback();
    }
};

/**
 * 正则特殊字符转义
 *
 * @param str
 */
export const escapeRegexp = (str?: string) => {
    if (!isString(str)) {
        return '';
    }
    const specialCharReg = /[|\\{}()[\]^$+*?.]/g;
    return str.replace(specialCharReg, '\\$&');
};

/**
 * 判断是否包含关键字
 * @param name 名称
 * @param keyword 关键字
 * @returns 是否包含关键字
 */
export const isSearchedByKeyword = (name: string, keyword: string) => {
    if (isEmpty(keyword)) {
        return true;
    }
    return new RegExp(escapeRegexp(keyword), 'i').test(name);
};

/**
 * 判断值是否为空
 *
 * @param value
 */
export const isStringEmpty = (value: any): value is '' | null | undefined => {
    return value === '' || isNil(value);
};

/**
 * html特殊字符转义
 *
 * @param text html文本
 */
export const escapeHtmlSpecialChar = (text: string) => {
    if (isEmpty(text)) {
        return '';
    }
    return text.replace(/[<>"&]/g, (match) => {
        switch (match) {
            case '<':
                return '&lt;';
            case '>':
                return '&gt;';
            case '&':
                return '&amp;';
            case '"':
                return '&quot;';
            default:
                return '';
        }
    });
};

/**
 * 是否是数字类型（包含可以参与数学运算的字符串）
 *
 * @see jQuery.isNumeric
 */
export const isNumeric = (num: any) => {
    const realStringObj = num && num.toString();
    return !isArray(num) && realStringObj - parseFloat(realStringObj) + 1 >= 0;
};

/**
 * 对象是否为空
 *
 * @static
 * @param {object} obj 对象
 * @returns {boolean} 返回是否为空
 */
export const isObjectEmpty = (obj: any) => {
    // 目标为 null
    if (isNil(obj)) return true;

    // 目标为有长度类型
    if (obj.length > 0) return false;
    if (obj.length === 0) return true;

    // 目标对象有属性
    for (const key in obj) {
        if (has(obj, key)) {
            return false;
        }
    }
    return isNaN(obj);
};

/**
 * 复制到剪贴板
 *
 * @param text
 * @param onCopySuccess
 */
export const copyToClipboard = (text: string, onCopySuccess?: () => void) => {
    if (!isEmpty(text)) {
        // 优先使用现代 Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    if (onCopySuccess) {
                        onCopySuccess();
                    }
                })
                .catch(() => {});
            return;
        }

        const textarea = document.createElement('textarea');
        textarea.style.background = 'transparent';
        textarea.style.color = 'transparent';
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        let success: boolean;
        textarea.setSelectionRange(0, text.length);
        textarea.focus();
        try {
            success = document.execCommand('copy');
        } catch (err) {
            success = false;
        }
        document.body.removeChild(textarea);
        if (success && onCopySuccess) {
            onCopySuccess();
        }
    }
};

/**
 * 关键词匹配
 *
 * @param text
 * @param keywords
 */
export const matchOneOfKeywords = (text: string, keywords: string[]) => {
    if (isEmpty(keywords)) {
        return true;
    }
    return some(keywords, (key) => {
        return isSearchedByKeyword(text, key);
    });
};

/**
 * DataURL转Blob
 *
 * @param dataurl
 * @returns {Blob}
 */
export const dataURLtoBlob = (dataurl: string) => {
    const arr: any = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
};

export const getCookie = (key: string) => {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    let val = '';
    forEach(cookies, (item) => {
        const parts = item.split('=');
        if (parts[0] === key) {
            val = parts[1]!;
            return false;
        }
    });
    return val;
};

/** 判断值是否为Promise */
export const isPromise = <T>(value: T | Promise<T>): value is Promise<T> => {
    if (!value) return false;
    return typeof value === 'object' && 'then' in value && typeof value.then === 'function';
};

/**
 * 脚本加载状态缓存，防止重复加载和竞态条件
 */
const scriptLoadingCache = new Map<string, Promise<any>>();

/**
 * 异步加载脚本
 *
 * @param url
 */
export const asyncLoadScript = (url: string) => {
    // 如果已经有正在加载或已加载的 Promise，直接返回
    if (scriptLoadingCache.has(url)) {
        return scriptLoadingCache.get(url)!;
    }

    // 检查 DOM 中是否已经存在该脚本（处理页面刷新或手动添加的情况）
    // 注意：这里使用 includes 而不是 starts-with，因为 url 可能是相对路径或部分 URL
    const scripts = document.getElementsByTagName('script');
    for (const existingScript of scripts) {
        if (existingScript?.src?.includes(url)) {
            // 已存在的脚本，创建一个立即 resolve 的 Promise
            const resolvedPromise = Promise.resolve(null);
            scriptLoadingCache.set(url, resolvedPromise);
            return resolvedPromise;
        }
    }

    // 创建新的加载 Promise
    const loadPromise = new Promise<any>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.type = 'text/javascript';
        script.async = true;

        const onLoad = () => {
            cleanup();
            resolve(null);
        };

        const onError = (error: any) => {
            cleanup();
            // 加载失败时从缓存中移除，允许重试
            scriptLoadingCache.delete(url);
            reject(error);
        };

        const cleanup = () => {
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
        };

        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
        document.head.appendChild(script);
    });

    // 缓存 Promise
    scriptLoadingCache.set(url, loadPromise);
    return loadPromise;
};
